import type { Scene } from '@lumen5/luminary';

import { Tool } from '../tools/Tool';
import type {
    EnhancedToolResult,
    ImageContent,
    TextContent,
    ToolDefinition,
    ToolExecutionContext,
} from '../types';

import {
    formatNarration,
    formatNarrationWindow,
    formatSceneNarration,
    narrationWindowAt,
    type TimedWord,
    timeNarrationWords,
} from './htmlDesignContext';
import {
    captureSceneFrames,
    captureSceneScreenshot,
    MOMENT_PROGRESS,
    SCREENSHOT_MAX_EDGE,
} from './sceneScreenshot';
import { WriteHtmlAssetTool } from './WriteHtmlAssetTool';

import { getHtmlDesignTarget } from 'js/src/data/html-design';

interface CaptureSceneArgs {
    sceneIndex?: number;
    detail?: 'low' | 'high';
    moment?: keyof typeof MOMENT_PROGRESS;
    progress?: number;
}

/**
 * Renders a scene to a screenshot the agent can look at. Defaults to the scene the graphic lives
 * on, so the agent can (a) see the surrounding design before authoring and (b) re-check its own
 * result after `write_html_asset` — the composition composites into the frame, so the screenshot
 * shows the graphic in context. Pass `sceneIndex` to view a neighbor instead. The image rides along
 * inside the tool result's content.
 */
export class CaptureSceneTool extends Tool<CaptureSceneArgs> {
    public static readonly toolName = 'capture_scene';

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: CaptureSceneTool.toolName,
                description:
                    'Take a screenshot of a scene to see how it currently looks. Defaults to the ' +
                    'scene your graphic is on (your composition is included in the frame, so use ' +
                    `this to review your own result after ${WriteHtmlAssetTool.toolName}). Pass sceneIndex to ` +
                    'view a different scene, such as a neighbor, or moment to see motion over the ' +
                    "scene's timeline instead of a single frame.",
                parameters: {
                    type: 'object',
                    properties: {
                        sceneIndex: {
                            type: 'integer',
                            description:
                                'Optional 0-based scene index to capture. Omit to capture the ' +
                                'scene your graphic is on.',
                        },
                        detail: {
                            type: 'string',
                            enum: ['low', 'high'],
                            description:
                                "Screenshot detail. 'low' (default) is a cheap overview — enough " +
                                "to see layout and colors, but too small to read body text. Use 'high' " +
                                'whenever you need to read the copy you wrote or check whether an ' +
                                'element actually rendered.',
                        },
                        moment: {
                            type: 'string',
                            enum: ['intro', 'outro', 'full'],
                            description:
                                "A sequence of frames across the scene's timeline, to see motion — " +
                                "'intro' (entrance), 'outro' (exit, ending on the final frame) or " +
                                "'full' (spread across the whole scene). Omit for one still.",
                        },
                        progress: {
                            type: 'number',
                            description:
                                'Capture one still at this exact point instead, 0 (start) to 1 ' +
                                '(end) — for an instant no preset covers, such as the onset of a ' +
                                'word you are syncing to. At 0 nothing has animated in yet, so an ' +
                                'empty frame there is expected rather than a fault.',
                        },
                    },
                    required: [],
                },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Capturing scene';
    }

    public get statusIcon(): string {
        return 'photo_camera';
    }

    public async execute(
        args: CaptureSceneArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const { scene, sceneIndex } = this.resolveScene(args, context.conversationId);
        if (!scene) {
            return {
                result: 'Error: could not find a scene to capture.',
                isSuccess: false,
            };
        }

        const moment = args.moment;
        const maxEdge = args.detail === 'high' ? SCREENSHOT_MAX_EDGE.high : SCREENSHOT_MAX_EDGE.low;

        // A single still unless asked otherwise: `progress` names a different instant for that
        // still, `moment` is the only way to get several frames. If both arrive, the named instant
        // wins — there is no reading of "one exact frame plus a span" worth guessing at.
        const explicitProgress =
            typeof args.progress === 'number' && Number.isFinite(args.progress)
                ? Math.max(0, Math.min(1, args.progress))
                : undefined;

        if (moment === undefined && explicitProgress === undefined) {
            const imageData = await captureSceneScreenshot(this.luminary, scene, maxEdge);
            const resultContent: (TextContent | ImageContent)[] = [
                { type: 'text', text: `Screenshot of scene index ${sceneIndex}:` },
            ];
            // A single still is an overview, not a spoken instant, so attach the whole scene's timed
            // narration (empty for a text-on-media scene; plain text when timings can't be mapped).
            const narration = formatSceneNarration(scene);
            if (narration) {
                resultContent.push({ type: 'text', text: narration });
            }
            resultContent.push({ type: 'image_url', image_url: { url: imageData } });
            return {
                result: `Captured a screenshot of scene index ${sceneIndex} (single, ${maxEdge}px).`,
                resultContent,
            };
        }

        const progressPoints =
            explicitProgress !== undefined
                ? [explicitProgress]
                : MOMENT_PROGRESS[moment as keyof typeof MOMENT_PROGRESS];
        const frames = await captureSceneFrames(this.luminary, scene, progressPoints, maxEdge);
        if (!frames.length) {
            return {
                result: `Error: could not render scene index ${sceneIndex}.`,
                isSuccess: false,
            };
        }

        const transcript = scene.extractTranscript();
        const timed = transcript.text.trim() ? timeNarrationWords(scene, transcript.words) : null;

        const spanLabel = explicitProgress !== undefined ? `at ${frames[0].label}` : moment;
        const resultContent: (TextContent | ImageContent)[] = [
            {
                type: 'text',
                text:
                    explicitProgress !== undefined
                        ? `Scene index ${sceneIndex} — one frame at ${frames[0].label} of the scene's timeline:`
                        : `Scene index ${sceneIndex} — ${moment}, ${frames.length} frames across the scene's timeline:`,
            },
        ];
        // Narration present but its timings can't be mapped to scene progress: attach it once as
        // plain text rather than fabricating a per-frame percentage.
        if (transcript.text.trim() && !timed) {
            resultContent.push({ type: 'text', text: formatNarration(transcript.text) });
        }
        frames.forEach((frame, index) => {
            resultContent.push({
                type: 'text',
                text: frameLabel(frame.label, progressPoints[index], timed),
            });
            resultContent.push({ type: 'image_url', image_url: { url: frame.dataUrl } });
        });

        const countLabel = frames.length === 1 ? '1 frame' : `${frames.length} frames`;
        return {
            result: `Captured ${countLabel} of scene index ${sceneIndex} (${spanLabel}, ${maxEdge}px).`,
            resultContent,
        };
    }

    private resolveScene(
        args: CaptureSceneArgs,
        conversationId: string | undefined,
    ): { scene: Scene | undefined; sceneIndex: number } {
        if (typeof args.sceneIndex === 'number') {
            return { scene: this.luminary.scenes[args.sceneIndex], sceneIndex: args.sceneIndex };
        }
        const target = getHtmlDesignTarget(this.getState(), conversationId);
        const scene = target ? this.luminary.getSceneById(target.sceneId) : undefined;
        return { scene, sceneIndex: scene ? scene.globalIndex : -1 };
    }
}

/**
 * The label for a captured frame, folding in the narration spoken around its scene-progress point
 * when the scene's word timings are available: "at 50% (saying: grew **forty** percent):". Falls
 * back to the bare "at NN%:" label when there's no mappable narration.
 */
function frameLabel(label: string, progress: number, timed: TimedWord[] | null): string {
    if (!timed) {
        return `at ${label}:`;
    }
    const window = narrationWindowAt(timed, progress);
    if (!window) {
        return `at ${label}:`;
    }
    const verb = window.activeIndex >= 0 ? 'saying' : 'around';
    return `at ${label} (${verb}: ${formatNarrationWindow(window)}):`;
}
